import {isCarpetFamilyFinish} from '@artemis-group/aiw-contracts';
import {ServiceUnitInput} from './service-unit-input';
import {overrideServiceQuantity} from '../lib/proposal-skirting';
import {ProposalSkirtingRooms} from './proposal-skirting-rooms';
/**
 * THE WORK — everything on the proposal that is not a product.
 *
 * Jacky, 18 September, on the real O-00000005 draft: *"I am missing optional work for all the remove &
 * dispose, and there is also remove & not dispose (customer provide skip) … this should be registered
 * somewhere as Artemis service cost. and proposal; read the config. I can always add more to config. or
 * add a custom service where I define QTY, UOM, Rate per. any additional work I add in, I can tick entire
 * item is optional, typical with skirting, I offer 2 sub option … and I also add item that is subject to
 * variation, price is set, not added to pricing unless used"* — and *"remeber the accessories that is
 * binded to main product, customer will see indent line showing its included in the price"*.
 *
 * Two decisions this panel makes, because they are his:
 *
 *   · **Every line here is seeded from what the drawing already knows** — the floor area, the skirting
 *     run, the stair steps, the scotia the canvas recorded — and each one says where its quantity came
 *     from. Nothing is retyped that the canvas measured.
 *   · **The rates come from the `artemis_services` config**, which he edits, and a rate nobody has
 *     approved stays unset: the line then says so instead of quoting a figure nobody agreed. A custom
 *     line is free of all of that — his number, his unit, his rate.
 */
import {Button} from '@aiw/ui';
import {useQuery} from '@tanstack/react-query';
import {useEffect} from 'react';
import type {ProposalAccessory, ProposalContent, ProposalServiceLine, SkirtingHeightBand} from '@artemis-group/aiw-contracts';
import {ArtemisServices} from '@aiw/config';
import {authHeaders, fetchImpl} from '../lib/api';
import {loadPricingBase} from '../lib/proposal-pricing';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import {ACCESSORY_KINDS, accessoryKindLabel} from '../lib/accessory-kinds';
import {Link} from 'react-router-dom';
import {commonServiceQuantity,quickPickServices} from '../lib/common-services';
import {inputCls} from './job-bits';
import {NumberField} from './number-field';
import {workMeasurements,type WorkMeasurements} from '@artemis-group/aiw-contracts';
export {workMeasurements,type WorkMeasurements} from '@artemis-group/aiw-contracts';

type Service = ProposalServiceLine;
type Accessory = ProposalAccessory;

/** Staff-only audit of the last saved Proposal calculation. Product and supplier names remain catalogue data. */
export function AccessoryMaterialPanel({snapshot,dirty}:{snapshot:ProposalContent['material_quantities'];dirty:boolean}){
 const {t,lang}=useT();
 if(!snapshot.plans.length)return null;
 return <section className="rounded-lg border bg-card p-4">
  <h3 className="font-semibold">{t('material.title')}</h3>
  <p className="text-micro text-muted-foreground">{dirty?t('material.saveToRefresh'):t('material.acceptedCarry')}</p>
  <div className="mt-2 space-y-2">{snapshot.plans.map(plan=><div key={`${plan.kind}:${plan.variant_id}`} className="rounded border p-2 text-sm">
   <div className="flex flex-wrap justify-between gap-2"><strong>{plan.product_name} · {plan.variant_name}</strong><span>{plan.supplier_name}</span></div>
   <p>{t(plan.status==='resolved'?'material.calculation':'material.provisionalCalculation',{measured:plan.measured_lm,safety:plan.safety_lm??'—',piece:plan.stock_piece_length_m??'—',pieces:plan.pieces??'—'})}</p>
   {plan.source_document?.name&&<p className="text-micro text-muted-foreground">{t('material.source',{source:plan.source_document.name,date:plan.source_document.effective_date??'—'})}</p>}
   {plan.issues.length>0&&(lang==='en'?<ul className="mt-1 list-disc pl-5 text-amber-800">{plan.issues.map(issue=><li key={issue}>{issue}</li>)}</ul>:<p className="mt-1 text-amber-800">{t('material.unresolved')}</p>)}
  </div>)}</div>
 </section>;
}

export function WorkPanel({services, accessories, measurements, existingCarpet, carpetOnly, onChange}: {
  services: Service[];
  accessories: Accessory[];
  measurements: WorkMeasurements;
  existingCarpet:boolean;
  carpetOnly:boolean;
  onChange: (next: {services: Service[]; accessories: Accessory[]}) => void;
}) {
  const {t} = useT();
  // The rates are a config document, read here rather than baked into the bundle: he changes them in
  // one place and every proposal follows.
  const config = useQuery({
    queryKey: ['artemis-services'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const r = await fetchImpl(`${env.apiBase}/api/config/artemis_services`, {headers: await authHeaders()});
      if (!r.ok) throw Error('Service costs unavailable');
      const body = await r.json();
      return ArtemisServices.parse(body.body ?? body.data?.body ?? ArtemisServices.defaults);
    },
  });
  const rates = config.data ?? ArtemisServices.defaults;
  // The stair block lives with the CATALOGUE, not in the service-cost config: the price is the product's.
  // Same react-query key as the products panel, so this is a cache hit rather than a second read.
  const base = useQuery({queryKey: ['pricing-base'], queryFn: loadPricingBase, staleTime: 5 * 60_000});
  const stair = base.data?.stair ?? null;
  const rateOf = (id: string) => rates.services.find((s) => s.id === id)?.sell ?? null;
  const templateOf = (id: string) => rates.services.find((s) => s.id === id) ?? null;
  const carpetStairTemplate = templateOf('carpet_stair_install') ?? ArtemisServices.defaults.services.find(s => s.id === 'carpet_stair_install');

  const set = (next: Partial<{services: Service[]; accessories: Accessory[]}>) => onChange({services, accessories, ...next});
  const patchService = (id: string, patch: Partial<Service>) => set({services: services.map((s) => (s.id === id ? {...s, ...patch} : s))});
  const patchAccessory = (id: string, patch: Partial<Accessory>) => set({accessories: accessories.map((a) => (a.id === id ? {...a, ...patch} : a))});

  const removalOptions=(line:Service):Service['options']=>{
    const template=templateOf('remove_dispose');
    const saved=(id:string)=>line.options.find(o=>o.id===id);
    const configured=(id:string)=>template?.options.find(o=>o.id===id);
    const option=(id:string,label:string):Service['options'][number]=>{
      const old=saved(id),base=configured(id);
      return {id,label,rate:old?.rate??base?.sell??null,note:old?.note??base?.note??'',catalogue:old?.catalogue??base?.catalogue??null,included_height_band:old?.included_height_band??base?.included_height_band??null};
    };
    if(existingCarpet)return [
      {id:'free_carpet',label:t('work.remove.freeCarpet'),rate:0,note:t('work.remove.freeCarpetNote'),catalogue:null,included_height_band:null},
      option('dispose',t('work.remove.charged')),
    ];
    return (template?.options??[]).map(o=>option(o.id,o.label));
  };

  // A saved free line may outlive a change to the assessed existing floor. Rebuild
  // its choices from the current evidence before staff can show it to a customer.
  useEffect(()=>{
    const next=services.map(line=>{
      if(line.service_id!=='remove_dispose')return line;
      const options=removalOptions(line);
      return JSON.stringify(line.options)===JSON.stringify(options)?line:{...line,options,chosen_option:line.chosen_option==='free_carpet'&&!existingCarpet?null:line.chosen_option};
    });
    if(next.some((line,i)=>line!==services[i]))onChange({services:next,accessories});
  },[services,accessories,existingCarpet,config.data,onChange]);

  // Existing draft lines pre-date the catalogue field. Once the current config is available, attach
  // only a missing catalogue by stable option id; preserve every saved label, price and explicit choice.
  useEffect(() => {
    if (!config.data) return;
    let changed = false;
    const enriched = services.map((line) => {
      const template = line.service_id ? templateOf(line.service_id) : null;
      if (!template) return line;
      const options = line.options.map((option) => {
        const configured = template.options.find((item) => item.id === option.id);
        if (option.catalogue == null && configured?.catalogue) {
          changed = true;
          return {...option, catalogue: configured.catalogue, included_height_band:option.included_height_band??configured.included_height_band};
        }
        if(option.catalogue&&configured?.catalogue){
          let catalogueChanged=false;
          const items=option.catalogue.items.map(item=>{const fallbackItem=configured.catalogue!.items.find(x=>x.id===item.id);if(!fallbackItem)return item;const variants=item.variants.map(variant=>{const fallbackVariant=fallbackItem.variants.find(x=>x.id===variant.id);if(!fallbackVariant||variant.height_band!==null&&variant.price_adjustment_per_lm!==null)return variant;catalogueChanged=true;return {...variant,height_band:variant.height_band??fallbackVariant.height_band,price_adjustment_per_lm:variant.price_adjustment_per_lm??fallbackVariant.price_adjustment_per_lm};});return {...item,variants};});
          if(catalogueChanged||option.included_height_band===null){changed=true;return {...option,included_height_band:option.included_height_band??configured.included_height_band,catalogue:{...option.catalogue,items}};}
        }
        return option;
      });
      return changed ? {...line, options} : line;
    });
    if (changed) onChange({services: enriched, accessories});
  }, [config.data, services, accessories, onChange]);

  /** Add a line from its config template, with the quantity the drawing measured. */
  /**
   * THE STAIR LINE, PRICED FROM THE PRODUCT.
   *
   * The rate is the catalogue's own sell for the stair product that matches how the stairs are FINISHED
   * (hard floor $130/step, carpet $15/step — the platform's rows), not a number kept in config. Its two
   * materials follow the platform's rule: the nosing the accessory matching binds to the floor product,
   * and the extra planks at 0.75 m² per step, which is what a stair run consumes beyond the floor area.
   */
  function addStairLine(quantity: number) {
    if (carpetOnly) {
      const standard = carpetStairTemplate?.options.find(o => o.id === 'standard');
      if (!standard || standard.sell === null) return;
      const line: Service = {
        id: `carpet_stair_install-${services.length + 1}`,
        service_id: 'carpet_stair_install',
        name: t('work.carpetStair.standard'),
        unit: 'step', quantity, rate: standard.sell,
        optional: false, variation: false, offered: true,
        chosen_option: 'standard', options: [],
        note: t('work.carpetStair.standardNote'), source: 'canvas',
      };
      set({services: [...services.filter(s => s.service_id !== 'carpet_stair_install' && s.service_id !== 'stair_work'), line],
        accessories: accessories.filter(a => !a.id.startsWith('stair_planks:'))});
      return;
    }
    if (!stair) return;
    // A carpeted flight — roll OR tiles — is a carpet stair install ($/step); everything else is the hard-floor
    // stair product. Deliberate since 18 Oct 2026: "Carpet Tile" used to land here only because it contains "carpet".
    const product = isCarpetFamilyFinish(measurements.stairFinish) ? stair.carpet : stair.hard_floor;
    const line: Service = {
      id: `stair_work-${services.length + 1}`,
      service_id: 'stair_work',
      name: `${product.name} — ${quantity} steps`,
      unit: 'step',
      quantity,
      rate: product.sell,
      optional: false,
      variation: true,
      offered: true,
      chosen_option: null,
      options: [],
      note: t('work.stairNote', {product: product.name}),
      source: 'product',
    };
    /**
     * THE EXTRA PLANKS ARE INCLUDED IN THE STAIR PRICE — that is what the platform's own quotes show
     * ("Stair Material - Extra Planks qty 12 m², included: true"): the rule inherits the floor's material
     * cost and carries it INSIDE the stair T1, so a customer is never charged twice for the same floor.
     *
     * They travel as an accessory BOUND to the stair product, which is the line the customer reads
     * indented and "included in the price" — the scope is visible, the figure is not counted again.
     */
    const planks: Accessory = {
      id: `stair_planks:${product.product_id}`,
      name: `${stair.extra_planks.name} — ${Math.round(quantity * stair.extra_planks.per_step * 100) / 100} ${stair.extra_planks.unit}`,
      scope: 'site',
      area_id: '', run_ids: [],
      colour: null,
      on: true,
      bound_to: product.product_id,
      unit: stair.extra_planks.unit,
      quantity: Math.round(quantity * stair.extra_planks.per_step * 100) / 100,
      rate: null,
      note: t('work.planksNote'),
      kind: 'stair',
      product_id: product.product_id,
      variant_id: null,
    };
    // One stair line, and the planks it includes as a bound accessory.
    set({
      services: [...services.filter((s) => s.service_id !== 'stair_work'), line],
      accessories: [...accessories.filter((a) => !a.id.startsWith('stair_planks:')), planks],
    });
  }

  function chooseCarpetStair(line: Service, mode: 'standard' | 'lips') {
    const option = carpetStairTemplate?.options.find(o => o.id === mode);
    if (!option || option.sell === null) return;
    patchService(line.id, {chosen_option: mode, rate: option.sell,
      name: t(`work.carpetStair.${mode}`), note: t(`work.carpetStair.${mode}Note`), optional: false, variation: false});
  }

  function addFromConfig(serviceId: string, quantity: number, unitOverride?: Service['unit'], name?: string, variationOverride?: boolean) {
    // Removal is available for every floor. Reopen an existing line without resetting its agreed rate or quantity.
    const existingRemoval=serviceId==='remove_dispose'?services.find(s=>s.service_id===serviceId):undefined;
    if(existingRemoval){document.getElementById(`work-service-${existingRemoval.id}`)?.scrollIntoView({block:'center'});return;}
    const template = templateOf(serviceId);
    if (!template) return;
    const line: Service = {
      id: `${serviceId}-${services.length + 1}`,
      service_id: template.id,
      name: serviceId==='skirting'?template.name:name ?? template.name,
      unit: unitOverride ?? template.unit,
      quantity: Math.round(quantity * 100) / 100,
      quantity_source:'assessment',
      rate: template.sell,
      optional: template.optional,
      // A seed may say it is a variation even when the template's own answer is quieter (door trimming).
      variation: variationOverride ?? template.variation,
      offered: true,
      chosen_option: null,
      options: template.options.map((o) => ({id: o.id, label: o.label, rate: o.sell, note: o.note, catalogue: o.catalogue, included_height_band:o.included_height_band})),
      note: template.note,
      source: 'canvas',
    };
    if(serviceId==='remove_dispose')line.options=removalOptions(line);
    set({services: [...services.filter((s) => s.service_id !== template.id || s.source === 'custom'), line]});
  }

  /**
   * The accessories the drawing implies — and the two it does not.
   *
   * Jacky, 19 Sep, on O-00000004: *"wont allow adding trims or able selecting type of underlay or moisture
   * barrier under the product switch like previous platform"*. So the seed is no longer conditional on the
   * canvas: trims are offered even when the drawing shows none (a job still needs them at the doorways),
   * and a moisture barrier is offered on every job it can apply to. Each line now carries its `kind`, so
   * the switchboard under a product knows which switch it belongs to and can set its TYPE from the
   * catalogue.
   */
  function seedAccessories() {
    const next: Accessory[] = [...accessories];
    const has = (id: string) => next.some((a) => a.id === id);
    /**
     * THE STAIR'S OWN FITTINGS COME WITH THE STAIR WORK.
     *
     * Jacky, 19 Sep: *"same as stair nosing"* — after the door trim was settled as a variation. The
     * nosing and the extra planks are not lines anybody orders here: the platform's stair rule emits them
     * as dependencies of the stair install, carrying COST ONLY (`unit_sell: 0`, `included: true`), which is
     * what "included in the price" means on its quotes. So they sit with the stair work as included, and
     * never as a quantity to charge — the same shape as the bound accessory on a floor.
     */
    const stair = services.find((s) => s.service_id === 'stair_work');
    for (const [id, kind, name, unit] of [['stair_nosing', 'stair', t('work.accessory.nosing'), 'PCs'], ['stair_planks', 'stair', t('work.accessory.planks'), 'm²']] as const) {
      if (has(id)) continue;
      next.push({id, kind, name, scope: 'site', area_id: '', run_ids: [], colour: null, on: true, bound_to: stair?.id ?? 'stair_work',
        unit, quantity: 0, rate: null, note: t('work.accessory.withStairs'), product_id: null, variant_id: null});
    }
    if (measurements.scotiaDrawn && !has('scotia')) {
      next.push({id: 'scotia', kind: 'scotia', name: t('work.accessory.scotia'), scope: 'site', area_id: '', run_ids: [], colour: null, on: true,
        bound_to: null, unit: 'LM', quantity: measurements.scotiaRunM!, rate: null, note: t('work.scotiaNote'), product_id: null, variant_id: null});
    }
    if (!has('trims')) {
      next.push({id: 'trims', kind: 'trims', name: t('work.accessory.trims'), scope: 'site', area_id: '', run_ids: [], colour: null, on: false,
        bound_to: null, unit: 'LM', quantity: measurements.trimRunM, rate: null, note: t('work.trimsNote'), product_id: null, variant_id: null});
    }
    if (!has('underlay')) {
      next.push({id: 'underlay', kind: 'underlay', name: t('work.accessory.underlay'), scope: 'site', area_id: '', run_ids: [], colour: null, on: false,
        bound_to: null, unit: 'm²', quantity: measurements.floorAreaM2, rate: null, note: '', product_id: null, variant_id: null});
    }
    if (!has('moisture_barrier')) {
      next.push({id: 'moisture_barrier', kind: 'moisture_barrier', name: t('work.accessory.moisture'), scope: 'site', area_id: '', run_ids: [], colour: null, on: false,
        bound_to: null, unit: 'm²', quantity: measurements.floorAreaM2, rate: null, note: '', product_id: null, variant_id: null});
    }
    set({accessories: next});
  }

  // ONE BUTTON PER PIECE OF WORK — his correction: the ways of doing it are sub-options inside the line,
  // not separate lines. ("combine 2 tier 1 item, but least customer not confused and thiking they need to
  // choose both")
  const seeds = [
    // The stair seed carries what the catalogue prices, so its rate is never maintained by hand.
    {id: 'stair_work', quantity: measurements.stairSteps, unit: 'step' as const, label: t('work.seed.stairs', {count: String(measurements.stairSteps)}), stair: true},
    {id: 'remove_dispose', quantity: measurements.floorAreaM2, label: t('work.seed.remove')},
    {id: 'skirting', quantity: measurements.skirtingRunM, unit: 'LM' as const, label: t('work.seed.skirting', {run: String(measurements.skirtingRunM)})},
    /**
     * DOOR TRIMMING IS A VARIATION — a rate, not a count.
     *
     * Jacky, 19 Sep: *"doors to trim is not 4. its by variation"*. How many leaves actually need trimming
     * is only known once the old floor is up and the new one is down: the drawing's doors are on the plan
     * as information (4 openings in the rooms being laid), never as a quantity to charge. So this seeds at
     * ZERO with its rate, and a quantity is his to type on the day if he wants to show what it would come
     * to — the line is approved by the customer and charged only if it is used.
     */
    {id: 'door_trim', quantity: 0, unit: 'each' as const, label: t('work.seed.doorTrim', {count: String(measurements.doorways)}), variation: true},
  ];

  return <section className="space-y-3 rounded-lg border bg-card p-4">
    <header>
      <h2 className="font-display text-sm font-semibold">{t('work.title')}</h2>
      <p className="text-micro text-muted-foreground">{t('work.blurb')}</p>
    </header>

    <section className="rounded-md border bg-muted/40 p-3" aria-labelledby="common-services-title">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div><h3 id="common-services-title" className="text-sm font-semibold">{t('work.quickPickTitle')}</h3><p className="text-micro text-muted-foreground">{t('work.quickPickNote')}</p></div>
        <Link className="text-micro text-primary underline" to="/service-library">{t('work.manageServices')}</Link>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{quickPickServices(rates.services).map(service=><button type="button" key={service.id} className="min-h-16 rounded-md border bg-card px-3 py-2 text-left hover:border-primary/60 hover:shadow-sm" onClick={()=>addFromConfig(service.id,commonServiceQuantity(service.basis,measurements),service.unit)}>
        <span className="block text-sm font-medium">{service.name}</span>
        <span className="mt-1 block text-micro text-muted-foreground">{service.sell===null?t('work.quickPickRateUnset',{unit:service.unit}):t('work.quickPickRate',{rate:String(service.sell),unit:service.unit})}</span>
      </button>)}</div>
    </section>

    {/* Seed from the drawing — the measurements are already there. */}
    <div className="flex flex-wrap gap-2">
      {seeds.filter(seed=>!carpetOnly||seed.id!=='stair_work').map((seed) => <Button key={seed.id} size="sm" variant="outline" onClick={() => ('stair' in seed && seed.stair ? addStairLine(seed.quantity) : addFromConfig(seed.id, seed.quantity, seed.unit, seed.label, 'variation' in seed ? seed.variation : undefined))}>
        {seed.label}
      </Button>)}
      {!carpetOnly&&<Button size="sm" variant="outline" onClick={seedAccessories}>{t('work.seed.accessories')}</Button>}
      <Button size="sm" variant="ghost" onClick={() => set({services: [...services, {
        id: `custom-${services.length + 1}`, service_id: null, name: t('work.customName'), unit: 'each', quantity: 1,
        rate: null, optional: false, variation: false, offered: true, chosen_option: null, options: [], note: '', source: 'custom',
      }]})}>{t('work.addCustom')}</Button>
    </div>

    {services.every(line=>line.id.startsWith('carpet-default-')&&line.service_id!=='remove_dispose') && <p className="text-sm text-muted-foreground">{t('work.none')}</p>}

    {services.filter(line=>!line.id.startsWith('carpet-default-')||line.service_id==='remove_dispose').map((line) => <div key={line.id} id={`work-service-${line.id}`} className="grid gap-2 rounded border p-3 md:grid-cols-12">
      <label className="md:col-span-4 text-sm">{t('work.name')}
        <input className={`${inputCls} mt-1 w-full`} value={line.name} onChange={(e) => patchService(line.id, {name: e.target.value})} maxLength={160} />
      </label>
      <label className="md:col-span-2 text-sm">{t('work.quantity')}
        <NumberField className={`${inputCls} mt-1 w-full`} value={line.quantity} onChange={(n) => patchService(line.id, overrideServiceQuantity(line,n))} />
      </label>
      <label className="md:col-span-1 text-sm">{t('work.unit')}
        <ServiceUnitInput className={`${inputCls} mt-1 w-full`} value={line.unit} onChange={(unit) => patchService(line.id, {unit})} />
      </label>
      <label className="md:col-span-2 text-sm">{t('work.rate')}
        <NumberField className={`${inputCls} mt-1 w-full`} value={line.rate} placeholder={t('work.rateUnset')}
          onChange={(n) => patchService(line.id, {rate: n})} />
      </label>
      <div className="md:col-span-3 flex flex-col justify-end gap-1 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={line.optional} onChange={(e) => patchService(line.id, {optional: e.target.checked, variation: e.target.checked ? false : line.variation})} />{t('work.optional')}</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={line.variation} onChange={(e) => patchService(line.id, {variation: e.target.checked, optional: e.target.checked ? false : line.optional})} />{t('work.variation')}</label>
      </div>
      <div className="md:col-span-12"><ProposalSkirtingRooms line={line} runs={measurements.skirtingRooms??[]} onChange={patch=>patchService(line.id,patch)}/></div>
      {line.service_id === 'carpet_stair_install' && <div className="md:col-span-12 flex flex-wrap gap-2 border-t pt-2">
        {(['standard', 'lips'] as const).map(mode => <Button key={mode} size="sm" variant={line.chosen_option === mode ? 'default' : 'outline'}
          onClick={() => chooseCarpetStair(line, mode)}>{t(`work.carpetStair.${mode}`)} · ${carpetStairTemplate?.options.find(o => o.id === mode)?.sell ?? '—'}/{t('work.carpetStair.step')}</Button>)}
        <span className="text-micro text-muted-foreground">{t('work.carpetStair.materialIncluded')}</span>
      </div>}
      {line.options.length > 0 && <div className="md:col-span-12 space-y-1 border-t pt-2">
        <p className="text-micro text-muted-foreground">{t('work.subOptions', {count: String(line.options.length)})}</p>
        {line.options.map((option, i) => <div key={option.id} className="grid gap-2 md:grid-cols-12">
          <input className={`${inputCls} md:col-span-6`} value={option.label} maxLength={160}
            onChange={(e) => patchService(line.id, {options: line.options.map((o, j) => (j === i ? {...o, label: e.target.value} : o))})} />
          {option.id==='free_carpet'?<span className="md:col-span-3 self-center text-sm font-medium">{t('work.remove.free')}</span>:<NumberField className={`${inputCls} md:col-span-3`} value={option.rate} placeholder={t('work.rateUnset')}
            onChange={(n) => patchService(line.id, {options: line.options.map((o, j) => (j === i ? {...o, rate: n} : o))})} />}
          <span className="md:col-span-3 self-center text-micro text-muted-foreground">
            {option.rate === null ? t('work.optionUnpriced') : `${line.quantity} ${line.unit} × ${option.rate} = ${Math.round(line.quantity * option.rate * 100) / 100}`}
          </span>
          {option.catalogue&&<label className="md:col-span-12 text-sm">{t('work.includedHeight')}
            <select className={`${inputCls} ml-2`} value={option.included_height_band??'standard'} onChange={e=>patchService(line.id,{options:line.options.map((o,j)=>j===i?{...o,included_height_band:e.target.value as SkirtingHeightBand}:o)})}>
              <option value="standard">{t('work.height.standard')}</option><option value="medium">{t('work.height.medium')}</option><option value="tall">{t('work.height.tall')}</option>
            </select><span className="ml-2 text-micro text-muted-foreground">{t('work.includedHeightHint')}</span>
          </label>}
        </div>)}
      </div>}
      <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-2 border-t pt-2 text-micro text-muted-foreground">
        <span>{line.source === 'canvas' ? t('work.fromCanvas') : line.source === 'custom' ? t('work.custom') : t('work.fromConfig')}</span>
        <span>{line.rate === null
          ? t('work.unpriced')
          : line.variation
            ? t('work.variationRate', {rate: String(line.rate), unit: line.unit})
            : `${line.quantity} ${line.unit} × ${line.rate} = ${Math.round(line.quantity * line.rate * 100) / 100}`}</span>
        <Button size="sm" variant="ghost" onClick={() => set({services: services.filter((s) => s.id !== line.id)})}>{t('common.close')}</Button>
      </div>
    </div>)}

    <div className="border-t pt-2">
      <h3 className="text-sm font-medium">{t('work.accessories')}</h3>
      <p className="text-micro text-muted-foreground">{t('work.accessoriesBlurb')}</p>
      {/* ADD ONE BY ITS FAMILY. The switchboard under a product is where he chooses a TYPE from the
          catalogue; this is for a line that belongs on no product — a moisture barrier charged on its own,
          a trim the drawing never showed. Jacky, 19 Sep: *"wont allow adding trims"*. */}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <label className="text-sm">{t('work.addAccessory')}
          <select className={`${inputCls} ml-2`} value="" onChange={(e) => {
            const kind = e.target.value;
            if (!kind) return;
            const basis = kind === 'trims' ? measurements.trimRunM : kind === 'scotia' ? measurements.scotiaRunM??0
              : kind === 'stair' ? measurements.stairSteps : measurements.floorAreaM2;
            const unit = kind === 'trims' ? 'LM' : kind === 'scotia' ? 'LM' : kind === 'stair' ? 'step' : 'm²';
            set({accessories: [...accessories, {
              id: crypto.randomUUID(), kind, name: accessoryKindLabel(t, kind), scope: 'site' as const, area_id: '', run_ids: [],
              colour: null, on: true, bound_to: null, unit, quantity: Math.round(basis * 100) / 100, rate: null,
              note: '', product_id: null, variant_id: null,
            }]});
          }}>
            <option value="">{t('work.chooseFamily')}</option>
            {ACCESSORY_KINDS.map((kind) => <option key={kind} value={kind}>{accessoryKindLabel(t, kind)}</option>)}
          </select>
        </label>
      </div>
      {accessories.filter(a=>!a.bound_to&&!a.id.startsWith('carpet-trim:')).map((a) => <div key={a.id} className="mt-2 rounded border p-2">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex items-center gap-2 pb-1 text-sm"><input type="checkbox" checked={a.on} onChange={(e) => patchAccessory(a.id, {on: e.target.checked})} />{a.kind==='smoothedge'?t('accessory.carpet.smoothedge'):accessoryKindLabel(t, a.kind || a.id.split(':')[0] || '')}</label>
          <label className="min-w-[200px] flex-1 text-sm">{t('work.name')}
            <input className={`${inputCls} mt-1 w-full`} value={a.name} onChange={(e) => patchAccessory(a.id, {name: e.target.value})} maxLength={160} />
          </label>
          <label className="text-sm">{t('work.scope')}
            <select className={`${inputCls} mt-1 w-full`} value={a.scope === 'site' ? 'site' : a.area_id}
              onChange={(e) => {const areaId=e.target.value==='site'?'':e.target.value;const areaKind=['underlay','moisture_barrier','adhesive','levelling'].includes(a.kind??'');patchAccessory(a.id,{scope:areaId?'area':'site',area_id:areaId,...(areaKind?{quantity:(areaId?measurements.floors.find(f=>f.area_id===areaId)?.area_m2??0:measurements.floorAreaM2),quantity_source:'assessment'}:{})});}}>
              <option value="site">{t('work.wholeSite')}</option>
              {measurements.floors.map((f) => <option key={f.area_id} value={f.area_id}>{f.name}</option>)}
            </select>
          </label>
          <label className="text-sm">{t('work.colour')}
            <input className={`${inputCls} mt-1 w-40`} value={a.colour ?? ''} onChange={(e) => patchAccessory(a.id, {colour: e.target.value || null})} maxLength={160} />
          </label>
          {/* QTY AND RATE, WHICH THE ROW NEVER HAD. Without them a switched-on accessory could only ever
              be "unpriced", and an unpriced line blocks the customer's copy — so "adding trims" ended in a
              wall. The quantity is seeded from the drawing and his to change; the rate is his, and leaving
              it empty when the line is bound to a product is correct (the product's price carries it). */}
          <label className="text-sm">{t('work.quantity')}
            <NumberField className={`${inputCls} mt-1 w-24`} value={a.quantity} onChange={(n) => patchAccessory(a.id, {quantity: n,quantity_source:'manual'})} />
            <span className="ml-1 text-micro text-muted-foreground">{a.unit}</span>
          </label>
          <label className="text-sm">{t('work.rate')}
            <NumberField className={`${inputCls} mt-1 w-24`} value={a.rate} placeholder={a.bound_to ? t('work.included') : t('work.rateUnset')}
              onChange={(n) => patchAccessory(a.id, {rate: n})} />
          </label>
          <Button size="sm" variant="ghost" onClick={() => set({accessories: accessories.filter((x) => x.id !== a.id)})}>{t('common.remove')}</Button>
        </div>
        {/* IT GOES WITH THE PRODUCT, SO IT IS TICKED ON THE PRODUCT.
            Jacky, 19 Sep: *"accessorie matching? and type product ID? I am not a machine. shouldnt that be
            tick inside the product instead."* The binding is chosen by ticking the accessory inside the
            product it belongs to (the switchboard on the product row), never by typing an id here. */}
        <p className="mt-1 text-micro text-muted-foreground">{a.bound_to ? t('work.boundNote') : t('work.standaloneHint')}</p>
      </div>)}
    </div>
  </section>;
}
